import { useGetPlanetaryTransitFromMoonQuery } from '@/store/api/planteryTransitApi';

import { useLazyGetGeneralPredictionQuery } from '@/store/api/Predictions/predictionApi';
import { useLazyGetChartImageQuery } from '@/store/api/BasicChart/chartImagesApi';
import { useLazyGetPredictionAudioQuery } from '@/store/api/PredictionAudio/predictionaudioApi';
import { useState, useEffect, useRef, type JSX } from 'react';
// import TransitPositionTableProps from '@/pages/user/components/Predictions/GeneralPrediction';
import AudioVisualizer from '@/pages/user/components/AudioVisualizer';
import WeeklyPrediction from '@/pages/user/components/Predictions/WeeklyPrediction';
import MonthlyPrediction from '@/pages/user/components/Predictions/MonthlyPrediction';
import DailyPredictionsView from '@/pages/user/components/DailyPredictions';
import Modal from '@/utils/modal.utils';
import { getApiErrorMessage } from '@/utils/apiError';
import PredictionEmptyState from '@/components/common/PredictionEmptyState';

// import Loader from '@/components/Loader';

interface HoroscopeData {
  title: string;
  content: string;
}

interface TabData {
  label: string;
  period: string;
  fallback?: HoroscopeData[];
  comingSoon?: boolean;
}

export interface PredictionResponse {
  status: 'PENDING' | 'SUCCESS' | 'FAILED';
  prediction: string;
  videoId?: string | null;
}

export default function Prediction({
  fetchedId,
  moonSign,
  apiData,
  receivedResponse,
  fetchUserType,
}: {
  fetchedId: string;
  moonSign: string;
  apiData: any;
  receivedResponse: boolean;
  fetchUserType?: string;
}): JSX.Element {
  const TABS: TabData[] = [
    {
      label: 'Daily Predictions',
      period: 'DAILY',
    },
    {
      label: 'Life Predictions',
      period: 'SEVEN_DAYS',
    },
    {
      label: 'Weekly Predictions',
      period: 'WEEKLY',
    },
    {
      label: 'Monthly Predictions',
      period: 'MONTHLY',
    },
    // {
    //   label: 'Annual Predictions',
    //   period: 'YEARLY',
    //   comingSoon: true,
    // },
  ];
  const isGuestUser = fetchUserType === 'Guest';

  // Personalised predictions are shown in English only.
  const language = 'English';

  const [getPredictionAudio] = useLazyGetPredictionAudioQuery();
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<string>(TABS[0]?.label ?? '');

  const [predictionData, setPredictionData] = useState<HoroscopeData[]>([]);
  const [loadingPrediction, setLoadingPrediction] = useState<boolean>(false);
  const [predictionStatus, setPredictionStatus] = useState<'PENDING' | 'SUCCESS' | 'FAILED' | ''>(
    ''
  );
  // The API's own message when the Life prediction request fails.
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rashiCharts, setRashiCharts] = useState<{
    south?: string;
    north?: string;
  }>({});
  const [planetLoading, setPlanetLoading] = useState(true);
  const [predictionId, setPredictionId] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState<string[]>([]);
  const [selectedTitle, setSelectedTitle] = useState('');

  const openModal = (images: (string | undefined)[], title: string) => {
    setModalImages(images.filter(Boolean) as string[]);
    setSelectedTitle(title);
    setModalOpen(true);
  };

  useEffect(() => {
    if (activeTab === 'Life Predictions') {
      setPlanetLoading(true);
    }
  }, [activeTab]);

  const [transitData, setTransitData] = useState<{
    date: string;
    transit: any[];
  }>({
    date: '',
    transit: [],
  });

  const zodiacRanges: Record<string, { start: number; end: number }> = {
    'Mesha / Aries': { start: 0, end: 30 },
    'Vrishabha / Taurus': { start: 30.01, end: 60 },
    'Mithuna / Gemini': { start: 60.01, end: 90 },
    'Karkataka / Cancer': { start: 90.01, end: 120 },
    'Simha / Leo': { start: 120.01, end: 150 },
    'Kanya / Virgo': { start: 150.01, end: 180 },
    'Tula / Libra': { start: 180.01, end: 210 },
    'Vrishchika / Scorpio': { start: 210.01, end: 240 },
    'Dhanur / Sagittarius': { start: 240.01, end: 270 },
    'Makar / Capricorn': { start: 270.01, end: 300 },
    'Kumbha / Aquarius': { start: 300.01, end: 330 },
    'Meena / Pisces': { start: 330.01, end: 360 },
  };

  const getRelativeDegree = (sign: string, globalDegree: number): string => {
    const zodiac = zodiacRanges[sign];
    if (!zodiac) return `${globalDegree.toFixed(2)}°`;

    const relative = globalDegree - zodiac.start;
    if (relative < 0 || relative > 30) {
      return `${globalDegree.toFixed(2)}°`;
    }
    return `${relative.toFixed(2)}°`;
  };

  const currentTab = TABS.find((tab) => tab.label === activeTab)!;

  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const [triggerPrediction] = useLazyGetGeneralPredictionQuery();

  const formatPredictionText = (raw: any): HoroscopeData[] => {
    const sectionMap: Record<string, string> = {
      overview_personality_traits_and_general_predictions:
        'Overview Personality Traits and General Predictions',
      career_and_profession: 'Career and Profession',
      wealth_investments_and_finances: 'Wealth, Investments and Finances',
      education_and_learning: 'Education and Learning',
      marriage_and_relationships: 'Marriage and Relationships',
      children: 'Children',
      health: 'Health',
      periods_of_caution: 'Periods of Caution',
      suggested_remedies: 'Suggested Remedies',
      general_predictions: 'General Predictions',
      wealth_and_investments: 'Wealth and Investments',
      personal_and_creative_life: 'Personal and Creative Life',
    };

    if (!raw) return [];

    const convertToBullets = (text: string) => {
      const items = text
        .split(/\n|•|-|\*/)
        .map((l) => l.replace(/^[•\-*]\s*/, '').trim())
        .filter(Boolean);
      return JSON.stringify({ type: 'bullets', items });
    };

    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const formatted: HoroscopeData[] = [];
      const prediction = parsed?.prediction ?? parsed;

      Object.entries(prediction).forEach(([key, value]: [string, any]) => {
        if (['ratings', 'heading', 'userId', 'predictionId', 'prediction'].includes(key)) return;
        if (!value) return;

        const title = (
          sectionMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
        ).replace(/_/g, ' ');

        if (typeof value === 'string' && value.trim()) {
          if (value.includes('•') || value.includes('\n-') || value.includes('\n*')) {
            formatted.push({ title, content: convertToBullets(value) });
          } else {
            formatted.push({ title, content: value.trim() });
          }
        } else if (Array.isArray(value) && value.length) {
          const items = value
            .filter((v: any) => typeof v === 'string')
            .map((v) => v.replace(/^[•\-*]\s*/g, '').trim());
          if (items.length) {
            formatted.push({
              title,
              content: JSON.stringify({ type: 'bullets', items }),
            });
          }
        } else if (typeof value === 'object' && value !== null) {
          const groups = Object.entries(value)
            .map(([subKey, subValue]) => {
              if (!subValue) return null;
              const items = Array.isArray(subValue)
                ? subValue.map((v) =>
                    String(v)
                      .replace(/^[•\-*]\s*/g, '')
                      .trim()
                  )
                : typeof subValue === 'string'
                  ? [subValue.replace(/^[•\-*]\s*/g, '').trim()]
                  : [];

              if (!items.length) return null;

              return {
                subtitle: subKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                items,
              };
            })
            .filter(Boolean) as { subtitle: string; items: string[] }[];

          if (groups.length > 0) {
            formatted.push({
              title: 'PERIODS OF CAUTION AND REMEDIES',
              content: JSON.stringify({ type: 'grouped-bullets', groups }),
            });
          }
          return;
        }
      });

      if (formatted.length > 0) return formatted;
    } catch (e) {
      console.error('Prediction parse error', e);
    }

    let clean = typeof raw === 'string' ? raw.trim() : String(raw).trim();
    if (clean.startsWith('"') && clean.endsWith('"')) {
      clean = clean.slice(1, -1);
    }
    clean = clean.replace(/\\n/g, '\n');

    const sectionTitles = [
      'Overview',
      'Education',
      'Education and Learning',
      'Career',
      'Career and Profession',
      'Health',
      'Wealth',
      'Wealth, Investments and Finances',
      'Marriage',
      'Marriage and Relationships',
      'Children',
      'Periods of Caution',
      'Suggested Remedies',
    ];

    const formatted: HoroscopeData[] = [];
    let currentTitle = '';
    let currentContent: string[] = [];

    const lines = clean.split('\n').map((l) => l.trim());

    for (const line of lines) {
      if (!line) continue;

      const matchedTitle = sectionTitles.find((title) =>
        line.toLowerCase().startsWith(title.toLowerCase())
      );

      if (matchedTitle) {
        // Store previous section
        if (currentTitle) {
          const joined = currentContent.join('\n');
          if (joined.includes('•')) {
            const items = joined
              .split('\n')
              .flatMap((l) => l.split('•'))
              .map((b) => b.trim())
              .filter(Boolean);
            formatted.push({
              title: currentTitle.replace(/_/g, ' '),
              content: JSON.stringify({
                type: 'bullets',
                items: items.map((i) => i.replace(/_/g, ' ')),
              }),
            });
          } else {
            formatted.push({
              title: currentTitle.replace(/_/g, ' '),
              content: joined.replace(/\n/g, ' ').replace(/_/g, ' ').trim(),
            });
          }
        }

        // Split current line at first colon to separate title/content
        const colonIndex = line.indexOf(':');
        if (colonIndex !== -1 && colonIndex < 40) {
          currentTitle = line.substring(0, colonIndex + 1).trim();
          const remainder = line.substring(colonIndex + 1).trim();
          currentContent = remainder ? [remainder] : [];
        } else {
          currentTitle = line;
          currentContent = [];
        }
      } else {
        currentContent.push(line);
      }
    }

    if (currentTitle) {
      const joined = currentContent.join('\n');
      if (joined.includes('•')) {
        const items = joined
          .split('\n')
          .flatMap((l) => l.split('•'))
          .map((b) => b.trim())
          .filter(Boolean);
        formatted.push({
          title: currentTitle.replace(/_/g, ' '),
          content: JSON.stringify({
            type: 'bullets',
            items: items.map((i) => i.replace(/_/g, ' ')),
          }),
        });
      } else {
        formatted.push({
          title: currentTitle.replace(/_/g, ' '),
          content: joined.replace(/\n/g, ' ').replace(/_/g, ' ').trim(),
        });
      }
    }

    return formatted;
  };

  const audioPollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (audioPollingTimeoutRef.current) clearTimeout(audioPollingTimeoutRef.current);
    };
  }, []);

  const fetchAudioWithPolling = async (predictionId: string, retryCount = 0) => {
    if (audioPollingTimeoutRef.current) clearTimeout(audioPollingTimeoutRef.current);
    setAudioLoading(true);
    setAudioError(null);
    try {
      const audioRes = await getPredictionAudio({
        predictionId,
        language: 'English',
      }).unwrap();

      const rawStatus = audioRes?.status;
      const status = typeof rawStatus === 'string' ? rawStatus.toUpperCase() : '';

      if (audioRes?.url) {
        setAudioSrc(audioRes.url);
        setAudioLoading(false);
        return;
      }

      if (status === 'DRAFT') {
        setAudioError(audioRes?.message || 'Audio not generated yet');
        setAudioLoading(false);
        return;
      }

      if (status === 'FAILED') {
        if (retryCount < 4) {
          audioPollingTimeoutRef.current = setTimeout(
            () => fetchAudioWithPolling(predictionId, retryCount + 1),
            2000
          );
        } else {
          setAudioError('Audio generation failed.');
          setAudioLoading(false);
        }
        return;
      }

      // If pending or no url, poll after 2 seconds
      audioPollingTimeoutRef.current = setTimeout(
        () => fetchAudioWithPolling(predictionId, retryCount),
        2000
      );
    } catch (err: any) {
      console.error('Audio fetch error:', err);

      const rawStatus = err?.data?.status;
      const status = typeof rawStatus === 'string' ? rawStatus.toUpperCase() : '';

      if (status === 'DRAFT') {
        setAudioError(err?.data?.message || 'Audio not generated yet');
        setAudioLoading(false);
        return;
      }

      if (status === 'FAILED') {
        if (retryCount < 4) {
          audioPollingTimeoutRef.current = setTimeout(
            () => fetchAudioWithPolling(predictionId, retryCount + 1),
            2000
          );
        } else {
          setAudioError('Audio generation failed.');
          setAudioLoading(false);
        }
        return;
      }

      // Handle server-side 500 error or other failed generations
      if (
        err?.status === 500 ||
        rawStatus === 500 ||
        String(err?.data?.message || '')
          .toLowerCase()
          .includes('fail')
      ) {
        setAudioError(err?.data?.message || 'Audio failed to generate.');
        setAudioLoading(false);
        return;
      }

      if (retryCount < 5) {
        audioPollingTimeoutRef.current = setTimeout(
          () => fetchAudioWithPolling(predictionId, retryCount + 1),
          2000
        );
      } else {
        setAudioError(err?.data?.message || 'Audio generation failed. Please retry.');
        setAudioLoading(false);
      }
    }
  };

  const fetchPrediction = async (retry = false) => {
    if (!fetchedId || currentTab.comingSoon) return;

    if (!retry) {
      setErrorMessage(null);
      setPredictionStatus('');
      setLoadingPrediction(true);
      setPredictionData([]);
      setAudioSrc(null);
    }

    try {
      const res = await triggerPrediction({
        userId: fetchedId,
      }).unwrap();

      const status = (res?.status?.toUpperCase() ?? 'FAILED') as 'PENDING' | 'SUCCESS' | 'FAILED';

      setPredictionStatus(status);

      if (status === 'SUCCESS') {
        const predictionObj = res?.prediction || {};

        setPredictionData(formatPredictionText(predictionObj));
        setLoadingPrediction(false);

        if (res?.id) {
          setPredictionId(res.id);
          fetchAudioWithPolling(res.id);
        }

        if (pollingRef.current) clearTimeout(pollingRef.current);
        return;
      }

      if (status === 'FAILED') {
        const message: unknown = 'message' in res ? res.message : undefined;
        setErrorMessage(typeof message === 'string' ? message : null);
        setLoadingPrediction(false);
        if (pollingRef.current) clearTimeout(pollingRef.current);
        return;
      }

      if (status === 'PENDING') {
        setLoadingPrediction(true);

        pollingRef.current = setTimeout(() => {
          fetchPrediction(true);
        }, 2000);
      }
    } catch (err) {
      console.error('Prediction fetch error:', err);
      setErrorMessage(getApiErrorMessage(err) ?? null);
      setPredictionStatus('FAILED');
      setLoadingPrediction(false);

      if (pollingRef.current) clearTimeout(pollingRef.current);
    }
  };

  useEffect(() => {
    if (pollingRef.current) clearTimeout(pollingRef.current);

    setPredictionData([]);
    setPredictionStatus('');
    setLoadingPrediction(false);

    if (!currentTab.comingSoon && currentTab.label === 'Life Predictions') {
      fetchPrediction();
    }
  }, [currentTab.period, fetchedId]);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, []);

  const [fetchChartImage] = useLazyGetChartImageQuery();

  useEffect(() => {
    async function fetchRashiCharts() {
      if (!fetchedId || !receivedResponse) return;

      try {
        const payload = {
          userId: String(fetchedId),
          imageType: 'png' as const,
        };

        const south = await fetchChartImage({
          ...payload,
          chartId: 'D1',
          chartType: 'south',
        }).unwrap();

        const north = await fetchChartImage({
          ...payload,
          chartId: 'D1',
          chartType: 'north',
        }).unwrap();

        setRashiCharts({
          south: south?.chartUrl,
          north: north?.chartUrl,
        });
      } catch (err) {
        console.error('Rashi chart fetch failed:', err);
      }
    }

    // Weekly, Monthly and Life all show the Rashi charts; load them once, on
    // whichever of those tabs is opened first (Daily is the default tab).
    const chartsLoaded = Boolean(rashiCharts.south || rashiCharts.north);
    if (activeTab !== 'Daily Predictions' && !chartsLoaded) {
      fetchRashiCharts();
    }
  }, [activeTab, fetchedId, receivedResponse, fetchChartImage, rashiCharts.south, rashiCharts.north]);

  //   const {
  //   data: transitResponse,
  //   isFetching: transitFetching,
  // } = useGetPlanetaryTransitFromMoonQuery(
  //   {
  //     userId: fetchedId,
  //     rashi: moonSign,
  //   },
  //   {
  //     skip:
  //       !fetchedId ||
  //       !moonSign ||
  //       activeTab !== 'Life Predictions',
  //   }
  // );
  // useEffect(() => {
  //   if (activeTab !== 'Life Predictions') return;

  //   if (!transitFetching) {
  //     if (transitResponse) {
  //       setTransitData({
  //         date: transitResponse.date || '',
  //         transit: Array.isArray(transitResponse.transit)
  //           ? transitResponse.transit
  //           : [],
  //       });
  //     }

  //     setPlanetLoading(false);
  //   }
  // }, [activeTab, transitFetching, transitResponse]);

  const referenceSign = moonSign || apiData?.BirthDetail?.astroData?.sign || '';

  return (
    <section className="w-full text-black">
      <div className="max-w-[1600px] mx-auto w-full bg-white text-black flex flex-col items-center">
        <h2 className="font-32 font-semibold font-spectral mb-4 bg-clip-text text-transparent bg-gradient-to-r from-[#082261] to-[#34065c] text-center">
          What Makes MeraAstro Special!
        </h2>
        <p className="text-xl font-normal font-lato bg-clip-text text-transparent bg-gradient-to-r from-[#2DACFF] to-[#035B95] mt-1 text-center">
          We don't do astrology only for Rashis and Lagnas, We do astrology for You!
        </p>

        <div className="flex flex-wrap items-center justify-center gap-4 mt-6 w-full max-w-[1200px]">
          <div className="flex flex-wrap justify-center gap-4">
            {TABS.map((tab) => (
              <button
                key={tab.label}
                onClick={() => setActiveTab(tab.label)}
                className={`px-4 py-2 rounded-[10px] font-spectral font-medium text-lg transition-all ${
                  activeTab === tab.label
                    ? 'bg-[linear-gradient(to_right,#082261,#34065c)] text-white'
                    : 'bg-white inner-shadow-blue text-black'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 space-y-6 ">
          {activeTab === 'Life Predictions' && predictionStatus === 'SUCCESS' && (
            <div className="w-full mx-auto border border-[#2DAAFD] rounded-xl overflow-hidden mb-8 shadow-sm p-6">
              <div className="flex flex-col gap-8">
                {/* Charts Section */}
                <div className="flex flex-col items-center">
                  <h4 className="font-spectral font-semibold text-[#001B6B] text-center text-lg mb-4">
                    Rashi Charts
                  </h4>
                  <div className="flex flex-row flex-wrap items-center justify-center gap-8">
                    {rashiCharts.south && (
                      <div className="flex flex-col items-center max-w-[350px]">
                        <p className="mb-2 text-sm font-semibold text-[#001B6B]">South Indian</p>
                        <img
                          src={rashiCharts.south}
                          alt="Rashi South Chart"
                          className="w-full rounded-md border p-2 border-[#2DAAFD] cursor-pointer transition-shadow duration-200"
                          onClick={() =>
                            openModal(
                              [rashiCharts.south, rashiCharts.north],
                              'Rashi Chart (South Indian & North Indian)'
                            )
                          }
                        />
                      </div>
                    )}

                    {rashiCharts.north && (
                      <div className="flex flex-col items-center max-w-[350px]">
                        <p className="mb-2 text-sm font-semibold text-[#001B6B]">North Indian</p>
                        <img
                          src={rashiCharts.north}
                          alt="Rashi North Chart"
                          className="w-full rounded-md border border-[#2DAAFD] cursor-pointer transition-shadow duration-200"
                          onClick={() =>
                            openModal(
                              [rashiCharts.south, rashiCharts.north],
                              'Rashi Chart (South Indian & North Indian)'
                            )
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Audio Section */}
                <div className="w-full pt-6 flex flex-col items-center">
                  {audioLoading ? (
                    <div className="text-center text-[#082261] font-spectral text-lg font-semibold animate-pulse">
                      Loading your personalized prediction audio...
                    </div>
                  ) : audioError ? (
                    <div className="text-center">
                      <p className="text-red-500 font-spectral text-lg mb-2">{audioError}</p>
                      {predictionId && (
                        <button
                          onClick={() => fetchAudioWithPolling(predictionId)}
                          className="px-4 py-2 bg-[#082261] text-white text-sm rounded-lg font-spectral hover:bg-[#2DAAFD] transition-colors"
                        >
                          Regenerate Audio
                        </button>
                      )}
                    </div>
                  ) : audioSrc ? (
                    <div className="w-full max-w-2xl">
                      <p className="font-18 font-semibold font-spectral mb-4 bg-clip-text text-transparent bg-gradient-to-r from-[#082261] to-[#34065c] text-center">
                        Personalized Audio Prediction
                      </p>
                      <AudioVisualizer audioSrc={audioSrc} />
                      <p className="mt-4 text-center text-sm text-gray-500 font-lato">
                        Click Play to hear your personalized life predictions.
                      </p>
                    </div>
                  ) : (
                    <div className="text-center text-gray-400 font-lato italic">
                      Audio prediction not available
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'Daily Predictions' &&
            (isGuestUser ? (
              <div className="text-center text-[#082261] font-spectral text-lg mt-10">
                Comming Soon ...
              </div>
            ) : (
              <DailyPredictionsView
                mode="personalised"
                userId={fetchedId}
                rashi={referenceSign}
              />
            ))}

          {activeTab === 'Weekly Predictions' &&
            (isGuestUser ? (
              <div className="text-center text-[#082261] font-spectral text-lg mt-10">
                Comming Soon ...
              </div>
            ) : (
              <WeeklyPrediction
                fetchedId={fetchedId}
                apiData={apiData}
                rashiCharts={rashiCharts}
                receivedResponse={receivedResponse}
                fetchUserType={fetchUserType}
                language={language}
              />
            ))}

          {activeTab === 'Monthly Predictions' &&
            (isGuestUser ? (
              <div className="text-center text-[#082261] font-spectral text-lg mt-10">
                Comming Soon ...
              </div>
            ) : (
              <MonthlyPrediction
                fetchedId={fetchedId}
                apiData={apiData}
                receivedResponse={receivedResponse}
                rashiCharts={rashiCharts}
                language={language}
              />
            ))}

          {activeTab === 'Annual Predictions' && (
            <div className="text-center text-[#082261] font-spectral text-lg mt-10">
              Coming soon...
            </div>
          )}

          {activeTab === 'Life Predictions' && !isGuestUser && (
            <>
              {currentTab.comingSoon ? (
                <div className="text-center text-[#082261] font-spectral text-lg">
                  Coming soon...
                </div>
              ) : predictionStatus === 'FAILED' ? (
                <PredictionEmptyState
                  title="Life prediction isn't ready yet"
                  description="The life prediction for this user hasn't been generated yet. It will appear here once it's ready."
                  detail={errorMessage}
                />
              ) : loadingPrediction || predictionStatus === 'PENDING' ? (
                <div className="text-center text-[#082261] font-spectral text-lg md:mt-20">
                  Decoding your unique planetary alignments to craft your personalized prediction.
                  Please wait a moment...
                </div>
              ) : predictionData.length ? (
                predictionData.map((section, idx) => {
                  let parsedContent: any = null;
                  try {
                    parsedContent = JSON.parse(section.content);
                  } catch {}

                  const isSectorLabel = (title: string) => {
                    const normalized = title.trim().toLowerCase();
                    return [
                      'career',
                      'wealth',
                      'money',
                      'relationships',
                      'children',
                      'health',
                      'education',
                      'overview',
                      'general predictions',
                      'suggested remedies',
                      'periods of caution',
                    ].some((lbl) => normalized.includes(lbl));
                  };

                  return (
                    <section key={idx}>
                      {!parsedContent && isSectorLabel(section.title) ? (
                        <p className="font-lato text-[#333333] text-[18px] leading-relaxed mt-3">
                          <span className="font-semibold text-[#001B6B]">{section.title}:</span>{' '}
                          {section.content}
                        </p>
                      ) : (
                        <>
                          {section.title && section.title !== '' && (
                            <h3 className="font-spectral text-[#001B6B] font-semibold text-[20px] uppercase tracking-wide mt-4">
                              {section.title}
                            </h3>
                          )}

                          {parsedContent?.type === 'grouped-bullets' ? (
                            <div className="mt-3 space-y-4">
                              {parsedContent.groups.map(
                                (group: { subtitle: string; items: string[] }, gi: number) => (
                                  <div key={gi}>
                                    <h4 className="font-lato font-semibold text-[18px] text-[#001B6B] mb-1">
                                      {group.subtitle}
                                    </h4>
                                    <ul className="pl-6 space-y-1 list-disc">
                                      {group.items.map((item: string, i: number) => (
                                        <li
                                          key={i}
                                          className="font-lato text-[#333333] text-[17px] leading-relaxed"
                                        >
                                          {item}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )
                              )}
                            </div>
                          ) : parsedContent?.type === 'bullets' ? (
                            <ul className="pl-6 mt-3 space-y-2 list-disc">
                              {parsedContent.items.map((item: string, i: number) => (
                                <li
                                  key={i}
                                  className="font-lato text-[#333333] text-[18px] leading-relaxed"
                                >
                                  {item}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="font-lato font-normal text-[#333333] text-[18px] leading-relaxed mt-2">
                              {section.content}
                            </p>
                          )}
                        </>
                      )}
                    </section>
                  );
                })
              ) : (
                <PredictionEmptyState
                  title="No prediction available"
                  description="No predictions available at the moment."
                />
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedTitle}
        widthClass="max-w-4xl"
      >
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 w-full">
          {modalImages.map((img, i) => (
            <div key={i} className="flex-1 w-full flex justify-center items-center">
              <img
                src={img}
                alt="Chart"
                className="max-h-[60vh] w-auto max-w-full rounded-md object-contain border border-[#2DAAFD] p-1 bg-white"
              />
            </div>
          ))}
        </div>
      </Modal>
    </section>
  );
}
